import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IReducer } from '../../../../../../redux/configureStore';
import Card from '../../../../../../shared/components/Card';
import {
  CardLabel,
  CardValue,
  ContentCard,
  LabelAndValueContainer,
} from '../../../../../../shared/components/Card/components/CardComponents';
import theme from '../../../../../../styles/theme';
import checkValue from '../../../../../../utils/values';
import CardWithTable from '../CardWithTable';
import BarChart from '../components/CardWithBarChart/components/BarChart';
import PdfTedPartnersPassedOn from '../TedDashboard/components/PdfTedPartnersPassedOn';
import Frame from './components/Frame';

function BoletoDashboard() {
  const profilePartnerState = useSelector(
    (reducer: IReducer) => reducer.partnerProfile
  );
  const boletoFaresState = useSelector(
    (reducer: IReducer) => reducer.boletoFares
  );
  const faresFilterState = useSelector(
    (reducer: IReducer) => reducer.faresFilter
  );
  const globalIsActive = Object.prototype.hasOwnProperty.call(
    boletoFaresState.income.value,
    'Fiducia'
  );

  let subtitle = '';

  const hasData = useMemo(() => {
    if (
      boletoFaresState.partnerPassedOn.length > 0 &&
      profilePartnerState.nivel === 2
    ) {
      return true;
    }
    return false;
  }, [boletoFaresState.partnerPassedOn, profilePartnerState.nivel]);

  switch (boletoFaresState.account) {
    case 'pf':
      subtitle = 'Movimento - Pessoa Física';
      break;
    case 'pj':
      subtitle = 'Movimento - Pessoa Jurídica';
      break;
    case 'todos':
      subtitle = 'Todos os tipos de contas';
      break;
    case 'consignada':
      subtitle = 'Consignada';
      break;
    case 'escrow':
      subtitle = 'Escrow';
      break;
    case 'TIB':
      subtitle = 'TIB';
      break;

    default:
      break;
  }

  function isPartnerGlobalBarChart() {
    if (faresFilterState.searched.partner === 'global') {
      return true;
    }
    return false;
  }

  return (
    <Frame.Base
      isActive={boletoFaresState.isActive}
      globalIsActive={globalIsActive}>
      <Card
        isActive
        title="Boletos Liquidados"
        subtitle={subtitle}
        display="flex"
        gridArea="boletosLiquided"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.mainColor}>
            {checkValue(boletoFaresState.finishedBoletos.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.mainColor}>
            {boletoFaresState.finishedBoletos.quantity}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        title="Tarifas Recebidas"
        subtitle={subtitle}
        display="flex"
        gridArea="faresReceived"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(boletoFaresState.faresReceived.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {boletoFaresState.faresReceived.quantity}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={globalIsActive}
        title="Tarifas Recebidas"
        subtitle={subtitle}
        display="flex"
        gridArea="faresReceived"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Global</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(boletoFaresState.fares.ambiente_382).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Fiducia</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(boletoFaresState.fares.Fiducia).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Parceiros</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(boletoFaresState.fares.Parceiro).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        title="Tarifas Repassadas"
        subtitle={subtitle}
        display="flex"
        gridArea="faresPassedOn"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.red}>
            {checkValue(boletoFaresState.faresPassedOn.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.red}>
            {boletoFaresState.faresPassedOn.quantity}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={globalIsActive}
        title="Resultados"
        subtitle={subtitle}
        display="flex"
        gridArea="income"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Global</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(
              boletoFaresState.income.value.ambiente_382 > 0
                ? boletoFaresState.income.value.ambiente_382
                : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Fiducia</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(
              boletoFaresState.income.value.Fiducia > 0
                ? boletoFaresState.income.value.Fiducia
                : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Parceiros</CardLabel>
          <CardValue
            color={
              boletoFaresState.income.value.Parceiro >= 0
                ? theme.colors.emphasis
                : theme.colors.red
            }>
            {checkValue(
              boletoFaresState.income.value.Parceiro > 0
                ? boletoFaresState.income.value.Parceiro
                : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        gridArea="income"
        title="Resultado"
        subtitle={subtitle}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <ContentCard hasOneData alignCenter justifyCenter>
          <CardLabel>Total</CardLabel>
          <CardValue hasOneData color={theme.colors.emphasis}>
            {checkValue(
              boletoFaresState.income.value > 0
                ? boletoFaresState.income.value
                : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </ContentCard>
      </Card>

      <CardWithTable
        isActive={hasData}
        gridArea="table"
        title="Repasses de Tarifas"
        subtitle={subtitle}
        valueColor="#18A3A8"
        data={boletoFaresState.partnerPassedOn}
        pdf={{
          title: 'Tarifas de TEDs Repassadas para Parceiros',
          body: <PdfTedPartnersPassedOn />,
        }}
      />

      <Card
        isActive={!isPartnerGlobalBarChart()}
        title="Tarifas Movimentadas nos Últimos 12 Meses"
        subtitle={subtitle}
        gridArea="lastTwelveMonths"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <BarChart
          data={boletoFaresState.lastTwelveMonths}
          hasOneData={false}
          firstValueColor={theme.colors.emphasis}
          secondValueColor={theme.colors.red}
          labelForTwoDatas={{
            firstLabel: `Tarifas recebidas   `,
            secondLabel: 'Tarifas repassadas',
          }}
        />
      </Card>

      <Card
        isActive={isPartnerGlobalBarChart()}
        title="Tarifas Movimentadas nos Últimos 12 Meses"
        subtitle={subtitle}
        gridArea="lastTwelveMonths"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <BarChart
          data={boletoFaresState.lastTwelveMonths}
          hasOneData={false}
          firstValueColor={theme.colors.emphasis}
          secondValueColor={theme.colors.blue}
          labelForTwoDatas={{
            firstLabel: `Tarifas recebidas (Fidúcia)   `,
            secondLabel: 'Tarifas repassadas (Parceiros)',
          }}
        />
      </Card>
    </Frame.Base>
  );
}

export default BoletoDashboard;
